import { z } from "zod";
import { GameVer, GAME_VER_ORD, lstring } from "./util/Lang";

/*
 * Grade
 */
export const GRADES = [
  "none",
  "d",
  "c",
  "b",
  "a",
  "s",
  "crystal_free",
] as const;
export const Grade = z.enum(GRADES);
export type Grade = z.infer<typeof Grade>;
export const GRADE_NAMES: Record<Grade, lstring> = {
  crystal_free: { en: "crystal_free", ru: "crystal_free" },
  none: { en: "No Grade", ru: "No Grade" },
  d: { en: "D", ru: "D" },
  c: { en: "C", ru: "C" },
  b: { en: "B", ru: "B" },
  a: { en: "A", ru: "A" },
  s: { en: "S", ru: "S" },
};

/*
 * Fish type
 */
export const FISH_TYPE = [
  "green",
  "jade",
  "blue",
  "yellow",
  "orange",
  "violet",
  "red",
  "white",
  "black",
] as const;
export const FishType = z.enum(FISH_TYPE);
export type FishType = z.infer<typeof FishType>;
export const FISH_TYPE_NAMES: Record<FishType, lstring> = {
  green: { en: "Green", ru: "Зеленый" },
  black: { en: "Black", ru: "Черный" },
  blue: { en: "Blue", ru: "Синий" },
  jade: { en: "Jade", ru: "Нефритовый" },
  orange: { en: "Orange", ru: "Оранжевый" },
  red: { en: "Red", ru: "Красный" },
  violet: { en: "Purple", ru: "Фиолетовый" },
  white: { en: "White", ru: "Белый" },
  yellow: { en: "Yellow", ru: "Желтый" },
};

/*
 * Weapon type
 */
export const WeaponType = z.enum([
  "sword",
  "blunt",
  "dagger",
  "bow",
  "pole",
  "fist",
  "dualfist",
  "dual",
  "etc",
  "none",
  // GF
  "dualdagger",
  "rapier",
  "crossbow",
  "ancientsword",
  "fishingrod",
  "flag",
  "ownthing",
]);
export type WeaponType = z.infer<typeof WeaponType>;
export const asWeaponType = WeaponType.parse;
export const WEAPON_TYPE_NAMES: Record<WeaponType, lstring> = {
  sword: { en: "Sword", ru: "Sword" },
  blunt: { en: "Blunt", ru: "Blunt" },
  dagger: { en: "Dagger", ru: "Dagger" },
  bow: { en: "Bow", ru: "Bow" },
  pole: { en: "Pole", ru: "Pole" },
  fist: { en: "Fist", ru: "Fist" },
  dualfist: { en: "Dual Fist", ru: "Dual Fist" },
  dual: { en: "Dual", ru: "Dual" },
  etc: { en: "Etc", ru: "Etc" },
  none: { en: "Not Weapon", ru: "Not Weapon" },
  // GF
  dualdagger: { en: "Dual Dagger", ru: "Dual Dagger" },
  rapier: { en: "Rapier", ru: "Rapier" },
  crossbow: { en: "Crossbow", ru: "Crossbow" },
  ancientsword: { en: "Ancient Sword", ru: "Ancient Sword" },
  fishingrod: { en: "Fishing Rod", ru: "Fishing Rod" },
  flag: { en: "Flag", ru: "Flag" },
  ownthing: { en: "ownthing", ru: "ownthing" },
};
export function getWeaponTypeGameVer(x: WeaponType): GameVer {
  switch (x) {
    case "sword":
    case "blunt":
    case "dagger":
    case "bow":
    case "pole":
    case "fist":
    case "dualfist":
    case "dual":
    case "etc":
    case "none":
      return "c1";
    // GF
    case "dualdagger":
    case "rapier":
    case "crossbow":
    case "ancientsword":
    case "fishingrod":
    case "flag":
    case "ownthing":
      return "gf";
  }
}
export const WEAPON_TYPES_BY_GAME_VER: Record<GameVer, WeaponType[]> = {
  c1: WeaponType.options.filter(
    (x) => GAME_VER_ORD[getWeaponTypeGameVer(x)] <= GAME_VER_ORD.c1
  ),
  c2: WeaponType.options.filter(
    (x) => GAME_VER_ORD[getWeaponTypeGameVer(x)] <= GAME_VER_ORD.c2
  ),
  c3: WeaponType.options.filter(
    (x) => GAME_VER_ORD[getWeaponTypeGameVer(x)] <= GAME_VER_ORD.c3
  ),
  c4: WeaponType.options.filter(
    (x) => GAME_VER_ORD[getWeaponTypeGameVer(x)] <= GAME_VER_ORD.c4
  ),
  c5: WeaponType.options.filter(
    (x) => GAME_VER_ORD[getWeaponTypeGameVer(x)] <= GAME_VER_ORD.c5
  ),
  il: WeaponType.options.filter(
    (x) => GAME_VER_ORD[getWeaponTypeGameVer(x)] <= GAME_VER_ORD.il
  ),
  gf: WeaponType.options.filter(
    (x) => GAME_VER_ORD[getWeaponTypeGameVer(x)] <= GAME_VER_ORD.gf
  ),
};

/*
 * Armor type
 */
export const ArmorType = z.enum(["light", "heavy", "magic", "sigil", "none"]);
export type ArmorType = z.infer<typeof ArmorType>;
export const asArmorType = ArmorType.parse;
export const ARMOR_TYPE_NAMES: Record<ArmorType, lstring> = {
  light: { en: "Light", ru: "Light" },
  heavy: { en: "Heavy", ru: "Heavy" },
  magic: { en: "Robe", ru: "Robe" },
  sigil: { en: "Sigil", ru: "Sigil" }, // GF
  none: { en: "Not Armor", ru: "Not Armor" },
};
export function getArmorTypeGameVer(x: ArmorType): GameVer {
  switch (x) {
    case "light":
    case "heavy":
    case "magic":
    case "none":
      return "c1";
    // GF
    case "sigil":
      return "gf";
  }
}
export const ARMOR_TYPES_BY_GAME_VER: Record<GameVer, ArmorType[]> = {
  c1: ArmorType.options.filter(
    (x) => GAME_VER_ORD[getArmorTypeGameVer(x)] <= GAME_VER_ORD.c1
  ),
  c2: ArmorType.options.filter(
    (x) => GAME_VER_ORD[getArmorTypeGameVer(x)] <= GAME_VER_ORD.c2
  ),
  c3: ArmorType.options.filter(
    (x) => GAME_VER_ORD[getArmorTypeGameVer(x)] <= GAME_VER_ORD.c3
  ),
  c4: ArmorType.options.filter(
    (x) => GAME_VER_ORD[getArmorTypeGameVer(x)] <= GAME_VER_ORD.c4
  ),
  c5: ArmorType.options.filter(
    (x) => GAME_VER_ORD[getArmorTypeGameVer(x)] <= GAME_VER_ORD.c5
  ),
  il: ArmorType.options.filter(
    (x) => GAME_VER_ORD[getArmorTypeGameVer(x)] <= GAME_VER_ORD.il
  ),
  gf: ArmorType.options.filter(
    (x) => GAME_VER_ORD[getArmorTypeGameVer(x)] <= GAME_VER_ORD.gf
  ),
};

/*
 * Slot
 */
export const Slot = z.enum([
  "rhand",
  "lrhand",
  "lhand",
  "chest",
  "legs",
  "feet",
  "head",
  "gloves",
  "back",
  "underwear",
  "none",
  "onepiece",
  "ear",
  "finger",
  "neck",
  // GF
  "rbracelet",
  "lbracelet",
  "hair",
  "hair2",
  "hairall",
  "alldress",
  "deco1",
  "waist",
]);
export type Slot = z.infer<typeof Slot>;
export function asSlot(data: unknown): Slot {
  if (data === "rfinger,lfinger") {
    return "finger";
  } else if (data === "rear,lear") {
    return "ear";
  }
  return Slot.parse(data);
}
export const SLOT_NAMES: Record<Slot, lstring> = {
  rhand: { en: "One Handed", ru: "One Handed" },
  lrhand: { en: "Two Handed", ru: "Two Handed" },
  lhand: { en: "Off Hand", ru: "Off Hand" },
  chest: { en: "Upper Body", ru: "Upper Body" },
  legs: { en: "Lower Body", ru: "Lower Body" },
  feet: { en: "Boots", ru: "Boots" },
  head: { en: "Headgear", ru: "Headgear" },
  gloves: { en: "Gloves", ru: "Gloves" },
  back: { en: "Back", ru: "Back" },
  underwear: { en: "Underwear", ru: "Underwear" },
  none: { en: "None", ru: "None" },
  onepiece: { en: "One Piece", ru: "One Piece" },
  ear: { en: "Earing", ru: "Earring" },
  finger: { en: "Ring", ru: "Ring" },
  neck: { en: "Necklace", ru: "Necklace" },
  // GF
  rbracelet: { en: "rbracelet", ru: "rbracelet" },
  lbracelet: { en: "lbracelet", ru: "lbracelet" },
  hair: { en: "hair", ru: "hair" },
  hair2: { en: "hair2", ru: "hair2" },
  hairall: { en: "hairall", ru: "hairall" },
  alldress: { en: "alldress", ru: "alldress" },
  deco1: { en: "deco1", ru: "deco1" },
  waist: { en: "Waist", ru: "Waist" },
};
export function getSlotGameVer(x: Slot): GameVer {
  switch (x) {
    case "rhand":
    case "lrhand":
    case "lhand":
    case "chest":
    case "legs":
    case "feet":
    case "head":
    case "gloves":
    case "back":
    case "underwear":
    case "none":
    case "onepiece":
    case "ear":
    case "finger":
    case "neck":
      return "c1";
    // GF
    case "rbracelet":
    case "lbracelet":
    case "hair":
    case "hair2":
    case "hairall":
    case "alldress":
    case "deco1":
    case "waist":
      return "gf";
  }
}
export const SLOTS_BY_GAME_VER: Record<GameVer, Slot[]> = {
  c1: Slot.options.filter(
    (x) => GAME_VER_ORD[getSlotGameVer(x)] <= GAME_VER_ORD.c1
  ),
  c2: Slot.options.filter(
    (x) => GAME_VER_ORD[getSlotGameVer(x)] <= GAME_VER_ORD.c2
  ),
  c3: Slot.options.filter(
    (x) => GAME_VER_ORD[getSlotGameVer(x)] <= GAME_VER_ORD.c3
  ),
  c4: Slot.options.filter(
    (x) => GAME_VER_ORD[getSlotGameVer(x)] <= GAME_VER_ORD.c4
  ),
  c5: Slot.options.filter(
    (x) => GAME_VER_ORD[getSlotGameVer(x)] <= GAME_VER_ORD.c5
  ),
  il: Slot.options.filter(
    (x) => GAME_VER_ORD[getSlotGameVer(x)] <= GAME_VER_ORD.il
  ),
  gf: Slot.options.filter(
    (x) => GAME_VER_ORD[getSlotGameVer(x)] <= GAME_VER_ORD.gf
  ),
};

/*
 * Race
 */
export const GameRaceUname = z.enum([
  "human",
  "elf",
  "darkelf",
  "dwarf",
  "orc",
]);
export type GameRaceUname = z.infer<typeof GameRaceUname>;

export const RaceUname = z.enum([
  "animal",
  "beast",
  "bug",
  "castle_guard",
  "construct",
  "darkelf",
  "demonic",
  "divine",
  "dragon",
  "dwarf",
  "elemental",
  "elf",
  "fairy",
  "giant",
  "human",
  "humanoid",
  "mercenary",
  "orc",
  "plant",
  "undead",
  "etc",
]);
export type RaceUname = z.infer<typeof RaceUname>;
export const RACE_NAMES: Record<RaceUname, lstring> = {
  animal: { en: "Animal", ru: "Animal" },
  beast: { en: "Beast", ru: "Beast" },
  bug: { en: "Bug", ru: "Bug" },
  castle_guard: { en: "Castle Guard", ru: "Castle Guard" },
  construct: { en: "Construct", ru: "Construct" },
  darkelf: { en: "Darkelf", ru: "Darkelf" },
  demonic: { en: "Demonic", ru: "Demonic" },
  divine: { en: "Divine", ru: "Divine" },
  dragon: { en: "Dragon", ru: "Dragon" },
  dwarf: { en: "Dwarf", ru: "Dwarf" },
  elemental: { en: "Elemental", ru: "Elemental" },
  elf: { en: "Elf", ru: "Elf" },
  fairy: { en: "Fairy", ru: "Fairy" },
  giant: { en: "Giant", ru: "Giant" },
  human: { en: "Human", ru: "Human" },
  humanoid: { en: "Humanoid", ru: "Humanoid" },
  mercenary: { en: "Mercenary", ru: "Mercenary" },
  orc: { en: "Orc", ru: "Orc" },
  plant: { en: "Plant", ru: "Plant" },
  undead: { en: "Undead", ru: "Undead" },
  etc: { en: "Etc", ru: "Etc" },
};

/*
 * Herbs
 */
export const HerbId = z.enum([
  "8600",
  "8601",
  "8602",
  "8603",
  "8604",
  "8605",
  "8606",
  "8608",
  "8610",
  "8607",
  "8609",
  "8612",
  "8613",
  "8614",
  "8611",
]);
export type HerbId = z.infer<typeof HerbId>;
export const HERB_NAMES: Record<HerbId, lstring> = {
  "8600": { en: "Herb of Life", ru: "Настойка Жизни" },
  "8601": { en: "Greater Herb of Life", ru: "Средняя Настойка Жизни" },
  "8602": { en: "Superior Herb of Life", ru: "Большая Настойка Жизни" },
  "8603": { en: "Herb of Mana", ru: "Настойка Маны" },
  "8604": { en: "Greater Herb of Mana", ru: "Средняя Настойка Маны" },
  "8605": { en: "Superior Herb of Mana", ru: "Большая Настойка Маны" },
  "8606": { en: "Herb of Power", ru: "Настойка Силы" },
  "8608": { en: "Herb of Atk. Spd.", ru: "Настойка Скорости Атаки" },
  "8610": {
    en: "Herb of Critical Attack.",
    ru: "Настойка Крит. Удара - Вероятность",
  },
  "8607": { en: "Herb of Magic", ru: "Настойка Магии" },
  "8609": { en: "Herb of Casting Spd.", ru: "Настойка Скорости Магии" },
  "8612": { en: "Herb of the Warrior", ru: "Настойка Воина" },
  "8613": { en: "Herb of the Mystic", ru: "Настойка Мистика" },
  "8614": { en: "Herb of Recovery", ru: "Настойка Восстановления" },
  "8611": { en: "Herb of Speed", ru: "Настойка Скорости" },
};

/*
 * Weakness
 */
export const WeaknessId = z.enum([
  "4274",
  "4275",
  "4276",
  "4279",
  "4280",
  "4281",
  "4282",
]);
export type WeaknessId = z.infer<typeof WeaknessId>;
export const WEAKNESS_NAMES: Record<WeaknessId, lstring> = {
  4274: { en: "Blunt Weapon", ru: "Дубины" },
  4275: { en: "Holy", ru: "Святость" },
  4276: { en: "Bow Weapon", ru: "Луки" },
  4279: { en: "Fire", ru: "Огонь" },
  4280: { en: "Water", ru: "Вода" },
  4281: { en: "Wind", ru: "Ветер" },
  4282: { en: "Earth", ru: "Земля" },
};

/*
 * Strong
 */
export const xRateId = z.enum(["x1_2", "x2", "x3", "x4", "x5"]);
export type xRateId = z.infer<typeof xRateId>;
export const StrongId = z.enum(["4311", "4303", "4304", "4305", "4306"]);

export type StrongId = z.infer<typeof StrongId>;

export const Strong_NAMES: Record<StrongId, lstring> = {
  4311: { en: "x1/2", ru: "x1/2" },
  4303: { en: "x2", ru: "x2" },
  4304: { en: "x3", ru: "x3" },
  4305: { en: "x4", ru: "x4" },
  4306: { en: "x5", ru: "x5" },
};

export const StrongIdNew = z.enum(["4408"]);
export type StrongIdNew = z.infer<typeof StrongIdNew>;

/*
 * NPC Types
 */
export const NpcType = z.enum(["boss", "warrior", "citizen", "pet", "summon"]);
export type NpcType = z.infer<typeof NpcType>;
export const NPC_TYPE_NAMES: Record<NpcType, lstring> = {
  boss: { en: "Boss", ru: "Босс" },
  citizen: { en: "Citizen", ru: "Горожанин" },
  warrior: { en: "Monster", ru: "Монстр" },
  summon: { en: "Summon", ru: "Саммон" },
  pet: { en: "Pet", ru: "Пет" },
};
